// Endpoint-based branding.
//
// One shared static build is served under multiple hostnames (console.verifyu.ai,
// console.theentropy.ai, console.vaultro.ai). The brand is resolved at RUNTIME from
// window.location.hostname — there is no per-host build. This module is the single
// source of truth for:
//   1. pre-login chrome  — browser tab title + favicon
//   2. the login page    — wordmark/logo + heading
//   3. the login guard   — an account whose brand != the endpoint's brand is rejected
//
// `key` MUST equal the backend brand key (see the Brands admin screen: a "stable
// identifier, lowercase, e.g. verifyu, fidaro"). The session's user.brand today only
// exposes `displayName`, so the guard also matches displayName via `aliases`,
// case-insensitively, as a fallback.

export const BRANDS = {
  verifyu: {
    key: "verifyu",
    displayName: "VerifyU",
    productName: "VerifyU Console",
    // Login-page logo. The VerifyU asset is a full wordmark (already includes the
    // name), so we don't also render the name text under it.
    logo: "/brand/verifyu-wordmark.png",
    logoHeight: 40,
    showNameWithLogo: false,
    // Tab favicon (the V mark).
    favicon: "/brand/verifyu-favicon.png",
    // Extra strings that identify this brand in user.brand.{key,displayName}.
    aliases: ["verifyu", "verify u"],
  },
  entropy: {
    key: "entropy",
    displayName: "Entropy",
    productName: "Entropy Console",
    // The only Entropy asset legible on the white login card is the pentagon mark
    // (the wordmark is white-on-transparent / has a black background), so we render
    // the mark plus the name as text.
    logo: "/brand/entropy-mark.png",
    logoHeight: 60,
    showNameWithLogo: true,
    favicon: "/brand/entropy-favicon.png",
    aliases: ["entropy", "the entropy", "theentropy"],
  },
  vaultro: {
    key: "vaultro",
    displayName: "Vaultro",
    productName: "Vaultro Console",
    // No Vaultro logo asset yet -> logo:null renders the displayName as a text
    // wordmark, and favicon:null keeps the neutral blank favicon. Drop a
    // /brand/vaultro-*.png in and set these to enable a graphic logo/favicon.
    logo: null,
    logoHeight: 40,
    showNameWithLogo: false,
    favicon: null,
    aliases: ["vaultro"],
  },
};

// hostname (lowercased) -> brand key. Every listed host is brand-scoped: the login
// guard STRICTLY enforces that an account's brand equals the host's brand (an account
// of any other brand is rejected). Hosts NOT listed here (localhost, raw IP, preview
// URLs) render DEFAULT_BRAND and impose NO restriction, so local dev keeps working.
const HOST_BRANDS = {
  "console.verifyu.ai": "verifyu",
  "console.theentropy.ai": "entropy",
  "console.vaultro.ai": "vaultro",
};

const DEFAULT_BRAND_KEY = "verifyu";

export function currentHostname() {
  return (typeof window !== "undefined" && window.location.hostname) || "";
}

// The brand a hostname is scoped to, or null when the host isn't brand-scoped.
export function brandForHost(hostname = currentHostname()) {
  const key = HOST_BRANDS[String(hostname).toLowerCase()];
  return key ? BRANDS[key] : null;
}

// The brand to RENDER (falls back to the default for unmapped hosts so the UI is
// never brand-less).
export function activeBrand(hostname = currentHostname()) {
  return brandForHost(hostname) || BRANDS[DEFAULT_BRAND_KEY];
}

const normalize = (s) => String(s || "").trim().toLowerCase();

// Comparable identifiers pulled from the session's brand object.
function accountBrandIds(userBrand) {
  if (!userBrand) return [];
  return [userBrand.key, userBrand.displayName].filter(Boolean).map(normalize);
}

// Cross-brand login guard.
// Returns a mismatch descriptor { host, accountBrand } when the endpoint is
// brand-scoped AND the account's brand is known AND it differs from the endpoint's
// brand. Returns null (i.e. "allow") when the host isn't brand-scoped or the account
// brand is unknown (fail-open, warned) — so a backend contract gap can't lock people
// out; it only ever BLOCKS on a positive, known mismatch.
export function brandMismatch(user, hostname = currentHostname()) {
  const hostBrand = brandForHost(hostname);
  if (!hostBrand) return null; // host not brand-scoped -> no restriction

  const ids = accountBrandIds(user?.brand);
  if (ids.length === 0) {
    console.warn(
      "[brand] account has no resolvable brand; skipping cross-brand guard for host",
      hostname
    );
    return null;
  }

  const expected = [hostBrand.key, ...hostBrand.aliases].map(normalize);
  const matches = ids.some((id) => expected.includes(id));
  return matches ? null : { host: hostBrand, accountBrand: user?.brand };
}

// Error thrown when a session is rejected by the cross-brand guard. Carries a
// user-facing `.message` so the login screen can surface it directly.
//
// The message is deliberately the SAME generic credentials error the backend returns,
// and says nothing about brands. Clients of one brand don't know the others exist, so
// naming the right Console — or even implying a different one exists — would disclose
// that the account belongs elsewhere.
//
// Nothing is logged here on purpose: the browser console is visible to the very person
// we're withholding this from. The real reason is logged SERVER-side by the backend's
// login guard, where the user can't read it.
export class BrandMismatchError extends Error {
  constructor(hostBrand) {
    super("Invalid email or password");
    this.name = "BrandMismatchError";
    this.hostBrand = hostBrand;
  }
}

function setFavicon(href) {
  if (typeof document === "undefined") return;
  let link = document.querySelector('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = href;
}

// Apply the host brand to the browser chrome (tab title + favicon). Called once at
// startup for the pre-login surfaces; ConsoleLayout still owns the title once a
// session's account brand is known (they agree, since the guard enforces host==account).
export function applyBrandChrome() {
  const b = activeBrand();
  if (b.productName && typeof document !== "undefined") document.title = b.productName;
  if (b.favicon) setFavicon(b.favicon);
}
